import { useState, useEffect } from 'react';
import { useAuthQuery } from '@/hooks/useAuthQuery';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Card, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Search, Plus, Trash2, Save, Info, Users, CheckSquare } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { ProjectUserPermission, ProjectPermissionsDialogProps, NewPermissionForm } from '@/types/auth';
import { useMutationWithToast } from '@/hooks/useQueryWithToast';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useIsMobile } from '@/hooks/use-mobile';
import { chunkedInQuerySimple, chunkedMutation } from '@/lib/queries/chunkedInQuery';

// Force rebuild

const ProjectPermissionsDialog = ({ projectId, projectName, isOpen, onClose, defaultCompany = 'all' }: ProjectPermissionsDialogProps) => {
  const isMobile = useIsMobile();
  
  const [searchEmail, setSearchEmail] = useState('');
  const [selectedCompany, setSelectedCompany] = useState<'all' | 'ENP' | 'Solid'>('all');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [singleUserSearch, setSingleUserSearch] = useState('');
  const [bulkPermissions, setBulkPermissions] = useState({
    can_view: true,
    can_edit: true,
    can_delete: true
  });
  const [newPermission, setNewPermission] = useState<NewPermissionForm>({
    user_ids: [],
    can_view: true,
    can_edit: true,
    can_delete: true
  });

  const { isAdmin } = useAdminCheck();

  // Auto-set company filter when dialog opens
  useEffect(() => {
    if (isOpen && defaultCompany && ['ENP', 'Solid'].includes(defaultCompany)) {
      setSelectedCompany(defaultCompany as 'ENP' | 'Solid');
    } else if (isOpen) {
      setSelectedCompany('all');
    }
  }, [isOpen, defaultCompany]);

  // Fetch available users for selection - secondary priority
  const { data: availableUsers } = useAuthQuery({
    queryKey: ['available-users', projectId, selectedCompany],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, company')
        .order('full_name')
        .limit(500);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!projectId && isOpen,
    requiresAuth: true,
    staleTime: 15 * 60 * 1000, // 15 minutes
  });

  // Fetch current permissions for the project - primary priority
  const { data: permissions, isLoading } = useAuthQuery({
    queryKey: ['project-permissions-list', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      
      const { data, error } = await supabase
        .from('project_user_permissions')
        .select('id, project_id, user_id, can_view, can_edit, can_delete, assigned_by, assigned_at, project_roles, updated_at')
        .eq('project_id', projectId)
        .limit(500);
      
      if (error) throw error;
      
      // Get user details and roles (using chunked query)
      const userIds = data.map(perm => perm.user_id);
      const profiles = await chunkedInQuerySimple<{ id: string; email: string; full_name: string }>(
        supabase,
        'profiles' as any,
        'id',
        userIds,
        'id, email, full_name'
      );

      // Get user roles (using chunked query)
      const userRoles = await chunkedInQuerySimple<{
        user_id: string;
        roles: { role_name: string; role_code: string } | null;
      }>(
        supabase,
        'user_roles' as any,
        'user_id',
        userIds,
        'user_id, roles:role_id(role_name, role_code)',
        (q) => q.eq('is_active', true)
      );
      
      return data.map(perm => {
        const profile = profiles?.find(p => p.id === perm.user_id);
        const roles = userRoles?.filter(ur => ur.user_id === perm.user_id)
          .map(ur => ur.roles?.role_name)
          .filter(Boolean) || [];
        
        return {
          ...perm,
          user_email: profile?.email || 'Unknown',
          user_name: profile?.full_name || 'Unknown User',
          user_roles: roles
        };
      }) as ProjectUserPermission[];
    },
    enabled: !!projectId && isOpen,
    requiresAuth: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Add permission mutation
  const addPermissionMutation = useMutationWithToast({
    mutationFn: async (permission: NewPermissionForm) => {
      if (!projectId || permission.user_ids.length === 0) {
        throw new Error('Không có user nào được chọn');
      }
      
      const currentUser = await supabase.auth.getUser();
      const permissionRecords = permission.user_ids.map(userId => ({
        project_id: projectId,
        user_id: userId,
        can_view: permission.can_view,
        can_edit: permission.can_edit,
        can_delete: permission.can_delete,
        assigned_by: currentUser.data.user?.id
      }));

      const { error } = await supabase
        .from('project_user_permissions')
        .insert(permissionRecords);
      
      if (error) throw error;
      return permissionRecords.length;
    },
    onSuccess: (count) => {
      setNewPermission({
        user_ids: [],
        can_view: true,
        can_edit: true,
        can_delete: true
      });
    },
    successMessage: `Đã thêm quyền thành công`,
    errorMessage: "Không thể thêm quyền",
    invalidateQueries: [['project-permissions-list', projectId], ['available-users', projectId]]
  });

  // Update permission mutation
  const updatePermissionMutation = useMutationWithToast({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ProjectUserPermission> }) => {
      const { data, error } = await supabase
        .from('project_user_permissions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    successMessage: "Đã cập nhật quyền",
    errorMessage: "Không thể cập nhật quyền",
    invalidateQueries: [['project-permissions-list', projectId]]
  });

  // Delete permission mutation
  const deletePermissionMutation = useMutationWithToast({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('project_user_permissions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    successMessage: "Đã xóa quyền",
    errorMessage: "Không thể xóa quyền",
    invalidateQueries: [['project-permissions-list', projectId]]
  });

  // Bulk permission assign mutation
  const bulkPermissionMutation = useMutationWithToast({
    mutationFn: async () => {
      if (!projectId || selectedUsers.length === 0) {
        throw new Error('Không có user nào được chọn');
      }

      // Remove existing permissions for these users (using chunked mutation)
      await chunkedMutation(
        supabase,
        'project_user_permissions',
        'user_id',
        selectedUsers,
        'delete'
      );

      // Add new permissions if any are granted
      if (bulkPermissions.can_view || bulkPermissions.can_edit || bulkPermissions.can_delete) {
        const currentUser = await supabase.auth.getUser();
        const permissionRecords = selectedUsers.map(userId => ({
          project_id: projectId,
          user_id: userId,
          can_view: bulkPermissions.can_view,
          can_edit: bulkPermissions.can_edit,
          can_delete: bulkPermissions.can_delete,
          assigned_by: currentUser.data.user?.id
        }));

        const { error } = await supabase
          .from('project_user_permissions')
          .insert(permissionRecords);
        
        if (error) throw error;
      }
    },
    onSuccess: () => {
      setSelectedUsers([]);
      setBulkPermissions({ can_view: true, can_edit: true, can_delete: true });
    },
    successMessage: `Đã cấp quyền cho ${selectedUsers.length} người dùng`,
    errorMessage: "Không thể cấp quyền hàng loạt",
    invalidateQueries: [['project-permissions-list', projectId], ['available-users', projectId, selectedCompany]]
  });

  // Ensure permissions is always treated as an array
  const permissionList = Array.isArray(permissions) ? permissions : [];
  const filteredPermissions = permissionList.filter(perm =>
    perm.user_email?.toLowerCase().includes(searchEmail.toLowerCase()) ||
    perm.user_name?.toLowerCase().includes(searchEmail.toLowerCase())
  );

  // Filter out users who already have permissions
  const usersWithPermissions = permissionList.map(p => p.user_id);
  const filteredAvailableUsers = availableUsers?.filter(user => {
    // Exclude users who already have permissions
    const hasPermission = usersWithPermissions.includes(user.id);
    
    // Filter by company if not 'all'
    const matchesCompany = selectedCompany === 'all' || user.company === selectedCompany;
    
    return !hasPermission && matchesCompany;
  }) || [];

  // Filter for single permission tab search
  const filteredSingleTabUsers = filteredAvailableUsers.filter(user => {
    if (!singleUserSearch.trim()) return true;
    const searchLower = singleUserSearch.toLowerCase();
    return (
      user.full_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  // Handle bulk selection
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(filteredAvailableUsers.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserSelection = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
    }
  };

  const isAllSelected = filteredAvailableUsers.length > 0 && selectedUsers.length === filteredAvailableUsers.length;
  const isSingleAllSelected = filteredAvailableUsers.length > 0 && newPermission.user_ids.length === filteredAvailableUsers.length;

  const handleSingleUserSelection = (userId: string, checked: boolean) => {
    if (checked) {
      setNewPermission(prev => ({ ...prev, user_ids: [...prev.user_ids, userId] }));
    } else {
      setNewPermission(prev => ({ ...prev, user_ids: prev.user_ids.filter(id => id !== userId) }));
    }
  };

  const handleSingleSelectAll = (checked: boolean) => {
    if (checked) {
      setNewPermission(prev => ({ ...prev, user_ids: filteredAvailableUsers.map(user => user.id) }));
    } else {
      setNewPermission(prev => ({ ...prev, user_ids: [] }));
    }
  };

  const handlePermissionChange = (permissionId: string, field: keyof ProjectUserPermission, value: boolean) => {
    updatePermissionMutation.mutate({
      id: permissionId,
      updates: { [field]: value }
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`w-[95vw] max-w-6xl mx-auto max-h-[90vh] overflow-y-auto ${isMobile ? 'p-4' : ''}`}>
        <DialogHeader>
          <DialogTitle className={isMobile ? 'text-lg' : ''}>
            Quản lý quyền truy cập - {projectName}
          </DialogTitle>
        </DialogHeader>

        <div className={`${isMobile ? 'space-y-4' : 'space-y-6'}`}>
          {/* Company Filter */}
          <div className={`border rounded-lg ${isMobile ? 'p-3' : 'p-4'}`}>
            <h3 className="font-medium flex items-center gap-2 mb-3">
              <Users className="h-4 w-4" />
              Lọc theo công ty
            </h3>
            <RadioGroup 
              value={selectedCompany} 
              onValueChange={(value: 'all' | 'ENP' | 'Solid') => {
                setSelectedCompany(value);
                setSelectedUsers([]); // Clear selection when changing company
              }}
              className="flex flex-row gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all">Tất cả</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ENP" id="ENP" />
                <Label htmlFor="ENP">ENP</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Solid" id="Solid" />
                <Label htmlFor="Solid">Solid</Label>
              </div>
            </RadioGroup>
          </div>

          <Tabs defaultValue="single" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="single">Thêm quyền cụ thể</TabsTrigger>
              <TabsTrigger value="bulk">Cấp quyền hàng loạt</TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="space-y-4">
              <div className={`border rounded-lg ${isMobile ? 'p-3' : 'p-4'} ${isMobile ? 'space-y-3' : 'space-y-4'}`}>
                <h3 className="font-medium flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Thêm quyền cho user cụ thể
                  {selectedCompany !== 'all' && (
                    <Badge variant="outline" className="text-xs">
                      {selectedCompany} ({filteredAvailableUsers.length} users)
                    </Badge>
                  )}
                </h3>
                
                <div className="flex items-center space-x-3 mb-4">
                  <Checkbox
                    id="singleSelectAll"
                    checked={isSingleAllSelected}
                    onCheckedChange={handleSingleSelectAll}
                  />
                  <Label htmlFor="singleSelectAll" className="text-sm font-medium cursor-pointer">
                    Chọn tất cả ({filteredAvailableUsers.length} users)
                  </Label>
                  {newPermission.user_ids.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {newPermission.user_ids.length} đã chọn
                    </Badge>
                  )}
                </div>

                <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
                  <div>
                    <Label className="mb-2 block">Chọn người dùng</Label>
                    <div className="relative mb-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Tìm theo tên hoặc email..."
                        value={singleUserSearch}
                        onChange={(e) => setSingleUserSearch(e.target.value)}
                        className="pl-9 h-9"
                      />
                    </div>
                    <div className="border rounded-lg p-3 max-h-64 overflow-y-auto space-y-2">
                      {filteredSingleTabUsers.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-4">
                          {singleUserSearch ? 'Không tìm thấy người dùng' : 'Không có người dùng khả dụng'}
                        </div>
                      ) : (
                        filteredSingleTabUsers.map((user) => (
                          <div key={user.id} className="flex items-center space-x-3 p-2 hover:bg-muted rounded transition-colors">
                            <Checkbox
                              id={`single-user-${user.id}`}
                              checked={newPermission.user_ids.includes(user.id)}
                              onCheckedChange={(checked) => handleSingleUserSelection(user.id, !!checked)}
                            />
                            <Label
                              htmlFor={`single-user-${user.id}`}
                              className="flex-1 text-sm cursor-pointer flex items-center justify-between"
                            >
                              <span className="truncate">
                                {user.full_name || 'Chưa có tên'} <span className="text-muted-foreground text-xs">({user.email})</span>
                              </span>
                              <Badge variant="outline" className="text-xs ml-2 shrink-0">
                                {user.company || 'ENP'}
                              </Badge>
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Quyền</Label>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="newView"
                          checked={newPermission.can_view}
                          onCheckedChange={(checked) => setNewPermission(prev => ({ ...prev, can_view: !!checked }))}
                        />
                        <Label htmlFor="newView" className="text-sm">Xem</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="newEdit"
                          checked={newPermission.can_edit}
                          onCheckedChange={(checked) => setNewPermission(prev => ({ ...prev, can_edit: !!checked }))}
                        />
                        <Label htmlFor="newEdit" className="text-sm">Sửa</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="newDelete"
                          checked={newPermission.can_delete}
                          onCheckedChange={(checked) => setNewPermission(prev => ({ ...prev, can_delete: !!checked }))}
                        />
                        <Label htmlFor="newDelete" className="text-sm">Xóa</Label>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => addPermissionMutation.mutate(newPermission)}
                  disabled={newPermission.user_ids.length === 0 || addPermissionMutation.isPending || filteredAvailableUsers.length === 0}
                  className={`${isMobile ? 'w-full min-h-[44px]' : 'w-full md:w-auto'}`}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Thêm quyền {newPermission.user_ids.length > 0 && `(${newPermission.user_ids.length} users)`}
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="bulk" className="space-y-4">
              {/* Bulk Actions */}
              {filteredAvailableUsers.length > 0 && (
                <div className={`border rounded-lg ${isMobile ? 'p-3' : 'p-4'} ${isMobile ? 'space-y-3' : 'space-y-4'}`}>
                  <h3 className="font-medium flex items-center gap-2">
                    <CheckSquare className="h-4 w-4" />
                    Cấp quyền hàng loạt
                    {selectedCompany !== 'all' && (
                      <Badge variant="outline" className="text-xs">
                        {selectedCompany} ({filteredAvailableUsers.length} users)
                      </Badge>
                    )}
                  </h3>
                  
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="selectAll"
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                    />
                    <Label htmlFor="selectAll" className="text-sm">
                      Chọn tất cả users {selectedCompany !== 'all' ? selectedCompany : ''} ({filteredAvailableUsers.length})
                    </Label>
                  </div>

                  {selectedUsers.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm font-medium">
                            Quyền cho {selectedUsers.length} users đã chọn:
                          </Label>
                          {!isAdmin && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Info className="h-3 w-3 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Chỉ Admin mới có thể cấp quyền sửa và xóa</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="bulkView"
                              checked={bulkPermissions.can_view}
                              onCheckedChange={(checked) => setBulkPermissions(prev => ({ ...prev, can_view: !!checked }))}
                            />
                            <Label htmlFor="bulkView" className="text-sm">Xem</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="bulkEdit"
                              checked={bulkPermissions.can_edit}
                              disabled={!isAdmin}
                              onCheckedChange={(checked) => setBulkPermissions(prev => ({ ...prev, can_edit: !!checked }))}
                            />
                            <Label htmlFor="bulkEdit" className={`text-sm ${!isAdmin ? 'text-muted-foreground' : ''}`}>Sửa</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="bulkDelete"
                              checked={bulkPermissions.can_delete}
                              disabled={!isAdmin}
                              onCheckedChange={(checked) => setBulkPermissions(prev => ({ ...prev, can_delete: !!checked }))}
                            />
                            <Label htmlFor="bulkDelete" className={`text-sm ${!isAdmin ? 'text-muted-foreground' : ''}`}>Xóa</Label>
                          </div>
                        </div>

                        <Button
                          onClick={() => bulkPermissionMutation.mutate(undefined)}
                          disabled={bulkPermissionMutation.isPending || selectedUsers.length === 0}
                          className={`${isMobile ? 'w-full min-h-[44px]' : 'w-auto'}`}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          {bulkPermissionMutation.isPending ? 'Đang cấp quyền...' : `Cấp quyền cho ${selectedUsers.length} users`}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* User Selection List */}
                  {filteredAvailableUsers.length > 0 && (
                    <>
                      <Separator />
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        <Label className="text-sm font-medium">Chọn users:</Label>
                        <div className="grid grid-cols-1 gap-2">
                          {filteredAvailableUsers.map((user) => (
                            <div key={user.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`user-${user.id}`}
                                checked={selectedUsers.includes(user.id)}
                                onCheckedChange={(checked) => handleUserSelection(user.id, !!checked)}
                              />
                              <Label htmlFor={`user-${user.id}`} className="text-sm flex-1 cursor-pointer">
                                {user.full_name || 'Chưa có tên'} ({user.email})
                                <Badge variant="outline" className="ml-2 text-xs">
                                  {user.company || 'ENP'}
                                </Badge>
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Search */}
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            <Input
              placeholder="Tìm kiếm theo email hoặc tên..."
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              className="max-w-xs"
            />
          </div>

          {/* Permissions Table/Cards */}
          <div className="border rounded-lg">
            {isLoading ? (
              <div className="p-8 text-center">Đang tải...</div>
            ) : isMobile ? (
              <div className="space-y-3 p-3">
                {filteredPermissions.map((permission) => (
                  <Card key={permission.id}>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium">{permission.user_name}</div>
                            <div className="text-sm text-muted-foreground">{permission.user_email}</div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deletePermissionMutation.mutate(permission.id)}
                            disabled={deletePermissionMutation.isPending}
                            className="min-h-[36px]"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium mb-1">Vai trò</div>
                          <div className="flex flex-wrap gap-1">
                            {permission.user_roles?.map((role, index) => (
                              <Badge key={index} variant="secondary" className="text-xs">
                                {role}
                              </Badge>
                            )) || (
                              <Badge variant="outline" className="text-xs">
                                Chưa có vai trò
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <div className="text-sm font-medium mb-2">Quyền</div>
                          <div className="space-y-2">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={permission.can_view}
                                onCheckedChange={(checked) => handlePermissionChange(permission.id, 'can_view', !!checked)}
                              />
                              <span className="text-sm">Xem</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={permission.can_edit}
                                disabled={!isAdmin}
                                onCheckedChange={(checked) => handlePermissionChange(permission.id, 'can_edit', !!checked)}
                              />
                              <span className={`text-sm ${!isAdmin ? 'text-muted-foreground' : ''}`}>Sửa</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                checked={permission.can_delete}
                                disabled={!isAdmin}
                                onCheckedChange={(checked) => handlePermissionChange(permission.id, 'can_delete', !!checked)}
                              />
                              <span className={`text-sm ${!isAdmin ? 'text-muted-foreground' : ''}`}>Xóa</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-xs text-muted-foreground">
                          Ngày cấp: {new Date(permission.assigned_at).toLocaleDateString('vi-VN')}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Người dùng</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Vai trò</TableHead>
                    <TableHead>Quyền</TableHead>
                    <TableHead>Ngày cấp</TableHead>
                    <TableHead>Thao tác</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPermissions.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell className="font-medium">
                        {permission.user_name}
                      </TableCell>
                      <TableCell>{permission.user_email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {permission.user_roles?.map((role, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {role}
                            </Badge>
                          )) || (
                            <Badge variant="outline" className="text-xs">
                              Chưa có vai trò
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={permission.can_view}
                              onCheckedChange={(checked) => handlePermissionChange(permission.id, 'can_view', !!checked)}
                            />
                            <span className="text-sm">Xem</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={permission.can_edit}
                              disabled={!isAdmin}
                              onCheckedChange={(checked) => handlePermissionChange(permission.id, 'can_edit', !!checked)}
                            />
                            <span className={`text-sm ${!isAdmin ? 'text-muted-foreground' : ''}`}>Sửa</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={permission.can_delete}
                              disabled={!isAdmin}
                              onCheckedChange={(checked) => handlePermissionChange(permission.id, 'can_delete', !!checked)}
                            />
                            <span className={`text-sm ${!isAdmin ? 'text-muted-foreground' : ''}`}>Xóa</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {new Date(permission.assigned_at).toLocaleDateString('vi-VN')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deletePermissionMutation.mutate(permission.id)}
                          disabled={deletePermissionMutation.isPending}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            
            {filteredPermissions.length === 0 && !isLoading && (
              <div className="text-center py-8 text-muted-foreground">
                {searchEmail ? 'Không tìm thấy user nào' : 'Chưa có quyền nào được cấp cho dự án này'}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectPermissionsDialog;